import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ContactListOptions } from "@shared-core/data/adapter";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatCount } from "@shared-core/domain/formatters";
import {
  getApiBaseUrl,
  joinApiUrl,
  getAuthToken,
} from "@shared-core/data/api/client";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { paths } from "@shared-core/routing/routes";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Card } from "../app/components/primitives/card";
import { Users, Search, Download } from "lucide-react";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import { PersonCard } from "../app/components/domain/contacts/PersonCard";
import type {
  ContactCategory,
  ContactViewItem,
} from "@shared-core/data/api-types";

type CategoryFilter = "all" | ContactCategory;

const CATEGORY_OPTIONS: ReadonlyArray<{ id: CategoryFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "strategic", label: "Strategic" },
  { id: "key", label: "Key" },
  { id: "operational", label: "Operational" },
  { id: "unknown", label: "Unknown" },
];

type SegmentFilter = "all" | "strategic" | "key" | "client";

const SEGMENT_OPTIONS: ReadonlyArray<{ id: SegmentFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "strategic", label: "Strategic" },
  { id: "key", label: "Key" },
  { id: "client", label: "Client" },
];

type CustomerStatusFilter =
  "all" | "never_ordered" | "new" | "existing" | "lapsed" | "reactivated";

const CUSTOMER_STATUS_OPTIONS: ReadonlyArray<{
  id: CustomerStatusFilter;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "never_ordered", label: "Never ordered" },
  { id: "new", label: "New" },
  { id: "existing", label: "Existing" },
  { id: "lapsed", label: "Lapsed" },
  { id: "reactivated", label: "Reactivated" },
];

const PAGE_SIZE = 25;

type SortOption = "name-asc" | "name-desc" | "customer-asc";

const SORT_TO_PARAM: Record<SortOption, string> = {
  "name-asc": "full_name",
  "name-desc": "-full_name",
  "customer-asc": "customer_name,full_name",
};

function contactToPersonData(contact: ContactViewItem) {
  return {
    // Contact id is an integer; PersonCard keys/links treat it as an opaque
    // string identifier (shared shape with string-id prospect contacts).
    id: String(contact.id),
    name: contact.name,
    category: (contact.contact_category ?? "unknown") as ContactCategory,
    customerName: contact.customer?.name ?? null,
    email: contact.email_address || null,
    phone: contact.phone_number_1 || null,
    phone2: contact.phone_number_2 || null,
  };
}

export function ContactsPage() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") || undefined;
  const categoryFromUrl = searchParams.get("category");
  const categoryFilter: CategoryFilter = CATEGORY_OPTIONS.some(
    (o) => o.id === categoryFromUrl,
  )
    ? (categoryFromUrl as CategoryFilter)
    : "all";
  const segmentFromUrl = searchParams.get("segment");
  const segmentFilter: SegmentFilter = SEGMENT_OPTIONS.some(
    (o) => o.id === segmentFromUrl,
  )
    ? (segmentFromUrl as SegmentFilter)
    : "all";
  const customerStatusFromUrl = searchParams.get("customer_status");
  const customerStatusFilter: CustomerStatusFilter =
    CUSTOMER_STATUS_OPTIONS.some((o) => o.id === customerStatusFromUrl)
      ? (customerStatusFromUrl as CustomerStatusFilter)
      : "all";

  const [contacts, setContacts] = useState<ContactViewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [newInLast7Days, setNewInLast7Days] = useState<number | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [marketQuery, setMarketQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("name-asc");
  const [hasEmailOnly, setHasEmailOnly] = useState(false);
  const [hasPhoneOnly, setHasPhoneOnly] = useState(false);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const debouncedMarket = useDebouncedValue(marketQuery, 300);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (customerFilter) params.set("customer_id", customerFilter);
      if (hasEmailOnly) params.set("has_email", "true");
      if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
      if (debouncedMarket.trim()) params.set("market", debouncedMarket.trim());
      if (segmentFilter !== "all") params.set("segment_id", segmentFilter);
      if (customerStatusFilter !== "all")
        params.set("customer_status", customerStatusFilter);
      const url = `${joinApiUrl(getApiBaseUrl(), "views/contacts/export.csv")}?${params.toString()}`;
      const token = await getAuthToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const csv = await resp.text();
      capabilities.downloadTextFile("tharstern-contacts.csv", csv, "text/csv");
    } catch (err) {
      console.error("CSV export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [
    customerFilter,
    hasEmailOnly,
    debouncedSearch,
    debouncedMarket,
    segmentFilter,
    customerStatusFilter,
    capabilities,
  ]);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setCategoryFilter = useCallback(
    (next: CategoryFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "all") {
            params.delete("category");
          } else {
            params.set("category", next);
          }
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSegmentFilter = useCallback(
    (next: SegmentFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "all") {
            params.delete("segment");
          } else {
            params.set("segment", next);
          }
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setCustomerStatusFilter = useCallback(
    (next: CustomerStatusFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "all") {
            params.delete("customer_status");
          } else {
            params.set("customer_status", next);
          }
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset to page 1 when filters/sort/search change.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortBy,
    debouncedSearch,
    debouncedMarket,
    customerFilter,
    categoryFilter,
    segmentFilter,
    customerStatusFilter,
    hasEmailOnly,
    hasPhoneOnly,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const options: ContactListOptions = {
        sort: SORT_TO_PARAM[sortBy],
        page,
        pageSize: PAGE_SIZE,
      };
      if (customerFilter) options.customerId = customerFilter;
      if (categoryFilter !== "all") options.contactCategory = categoryFilter;
      if (hasEmailOnly) options.hasEmail = true;
      if (hasPhoneOnly) options.hasPhone = true;
      if (debouncedSearch.trim()) options.q = debouncedSearch.trim();
      if (debouncedMarket.trim()) options.market = debouncedMarket.trim();
      if (segmentFilter !== "all") options.segmentId = segmentFilter;
      if (customerStatusFilter !== "all")
        options.customerStatus = customerStatusFilter;

      try {
        const result = await adapter.getContactViews(options);
        if (cancelled) return;
        setContacts(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
        setNewInLast7Days(result.new_in_last_7_days ?? null);
      } catch (error) {
        console.error("Failed to load contacts:", error);
      } finally {
        if (!cancelled) setFirstLoad(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [
    adapter,
    customerFilter,
    categoryFilter,
    segmentFilter,
    customerStatusFilter,
    hasEmailOnly,
    hasPhoneOnly,
    sortBy,
    debouncedSearch,
    debouncedMarket,
    page,
  ]);

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading contacts...</div>
      </div>
    );
  }

  const customerName = customerFilter
    ? (contacts.find((c) => c.customer_id === customerFilter)?.customer?.name ??
      null)
    : null;

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1>
              {customerFilter
                ? `Contacts for ${customerName ?? "…"}`
                : "Contacts"}
            </h1>
            {customerFilter && (
              <CustomerScopeChip
                customerName={customerName}
                onDismiss={handleDismissCustomerScope}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {formatCount(total)} contact{total !== 1 ? "s" : ""}
            </span>
            {newInLast7Days !== null && newInLast7Days > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                {newInLast7Days} new in last 7 days
              </span>
            )}
            <button
              onClick={handleExportCsv}
              disabled={isExporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="size-3.5" />
              {isExporting ? "Exporting…" : "Download CSV"}
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <input
            type="text"
            placeholder="Market..."
            value={marketQuery}
            onChange={(e) => setMarketQuery(e.target.value)}
            className="w-full sm:w-40 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-3 py-2 rounded border border-border bg-card text-sm"
          >
            <option value="name-asc">Name (A–Z)</option>
            <option value="name-desc">Name (Z–A)</option>
            <option value="customer-asc">Customer name</option>
          </select>
        </div>

        {/* Category filter pills */}
        <div className="flex gap-2 flex-wrap">
          {CATEGORY_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setCategoryFilter(opt.id)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                categoryFilter === opt.id
                  ? "bg-primary text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Segment filter pills */}
        <div className="flex gap-2 flex-wrap">
          {SEGMENT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSegmentFilter(opt.id)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                segmentFilter === opt.id
                  ? "bg-primary text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Customer status filter pills */}
        <div className="flex gap-2 flex-wrap">
          {CUSTOMER_STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setCustomerStatusFilter(opt.id)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                customerStatusFilter === opt.id
                  ? "bg-primary text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Contact-method filter pills */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setHasEmailOnly((v) => !v)}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              hasEmailOnly
                ? "bg-primary text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            With email
          </button>
          <button
            onClick={() => setHasPhoneOnly((v) => !v)}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              hasPhoneOnly
                ? "bg-primary text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            With phone
          </button>
        </div>
      </div>

      {/* Contacts list */}
      {contacts.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="mb-2">No contacts found</h3>
          <p className="text-muted-foreground">
            {searchQuery
              ? "Try adjusting your search or filters"
              : "No contacts available"}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((contact) => {
            const detailPath = paths.contactDetail({
              id: contact.id,
              customerId: contact.customer_id,
            });
            return (
              <div
                key={`${contact.id}:${contact.customer_id}`}
                role="link"
                tabIndex={0}
                onClick={() => navigate(detailPath)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate(detailPath);
                }}
                className="cursor-pointer hover:opacity-90 transition-opacity"
              >
                <PersonCard person={contactToPersonData(contact)} />
              </div>
            );
          })}
        </div>
      )}

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
